import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Skeleton, Text } from '@mantine/core';
import HomeSection from '@/components/app/home/HomeSection';
import { marketCropPath } from '@/lib/agentDashboard';
import { formatMoney } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import type { MarketCrop } from '@/types/agentAnalytics';
import classes from './home.module.css';

interface AgentMarketSummaryProps {
  /** Культуры для показа: уже отобранные и упорядоченные (topMarketCrops). */
  readonly crops: readonly MarketCrop[];
  readonly loading: boolean;
}

// «Рынок сейчас» на «Главной» агента вместо «Лучших предложений»: культуры, где больше всего предложений о
// продаже, с типичной ценой. Строка ведёт на экран культуры в разделе «Рынок». Пустой рынок и сбой раздел скрывают:
// о сбое загрузки рынка сообщает блок «Рынок» панели («Нет данных») и сам раздел «Рынок».
export default function AgentMarketSummary({ crops, loading }: AgentMarketSummaryProps) {
  const { t } = useTranslation();
  const link = { to: ROUTES.APP_AGENT_DASHBOARD, label: t('app.home.market.all') };

  if (loading) {
    return (
      <HomeSection title={t('app.home.market.title')} link={link}>
        <Skeleton h={160} mt="md" aria-busy />
      </HomeSection>
    );
  }
  if (crops.length === 0) {
    return null;
  }

  return (
    <HomeSection title={t('app.home.market.title')} link={link}>
      {crops.map((crop) => (
        <Link key={crop.goodsType} to={marketCropPath(crop.goodsType)} className={`${classes.row} ${classes.item}`}>
          <Text fw={600}>{crop.goodsType}</Text>
          <Text size="sm" c="gray.7" className={classes.itemRight}>
            {[
              t('app.home.market.offers', { count: crop.sell?.count ?? 0 }),
              crop.sell?.median != null ? t('app.home.market.median', { price: formatMoney(crop.sell.median) }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </Link>
      ))}
    </HomeSection>
  );
}
